import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import Box from '@mui/material/Box';
import { AuthProvider } from './context/AuthContext';
import AuthModal from './components/AuthModal';
import UpgradeModal from './components/UpgradeModal';
import AdminViewBar from './components/AdminViewBar';
import MatrixRain from './components/MatrixRain';
import FootballPass from './components/FootballPass';
// Phase C.1a — LandingPage.js (the old NFL analytics/fantasy root
// experience) is deliberately left completely in place and unmodified,
// per this phase's explicit "no wholesale deletion" instruction — it is
// simply no longer imported/routed here. FFNLandingPage.js is the new
// root landing experience; see its own header for the full reasoning.
import FFNLandingPage from './pages/FFNLandingPage';
import NFLPage from './pages/NFLPage';
import InTheWorksPage from './pages/InTheWorksPage';
import TeamPage from './pages/TeamPage';
import PricingPage from './pages/PricingPage';
import UpgradeSuccessPage from './pages/UpgradeSuccessPage';
import AdminPage from './pages/AdminPage';
import CollegePage from './pages/CollegePage';
import QBComparePage from './pages/QBComparePage';
import ToolsPage from './pages/ToolsPage';
import FantasyPage from './pages/FantasyPage';
import GettingStartedPage from './pages/GettingStartedPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyAthletesPage from './pages/athletes/MyAthletesPage';
import JoinTeamPage from './pages/athletes/JoinTeamPage';
import CreateAthletePage from './pages/athletes/CreateAthletePage';
import AthleteProfilePage from './pages/athletes/AthleteProfilePage';
import EditAthletePage from './pages/athletes/EditAthletePage';
import TestingSessionFormPage from './pages/athletes/TestingSessionFormPage';
import PersonalTestingPage from './pages/athletes/PersonalTestingPage';
import MyTeamsPage from './pages/teams/MyTeamsPage';
import CreateTeamPage from './pages/teams/CreateTeamPage';
import TeamDetailPage from './pages/teams/TeamDetailPage';
import TeamStaffPage from './pages/teams/TeamStaffPage';
import TeamSettingsPage from './pages/teams/TeamSettingsPage';
import TeamRosterPage from './pages/teams/TeamRosterPage';
import RosteredAthletePage from './pages/teams/RosteredAthletePage';
import TeamTestingPage from './pages/teams/TeamTestingPage';
import TeamSchedulePage from './pages/teams/TeamSchedulePage';
import TeamBoardPage from './pages/teams/TeamBoardPage';
import TeamPlaybookPage from './pages/teams/TeamPlaybookPage';
import MyProgramsPage from './pages/programs/MyProgramsPage';
import ProgramDashboardPage from './pages/programs/ProgramDashboardPage';
import CreateSeasonTeamPage from './pages/programs/CreateSeasonTeamPage';
import ManageProgramPage from './pages/programs/ManageProgramPage';
import CreateProgramPage from './pages/programs/CreateProgramPage';
import ProgramSeasonsPage from './pages/programs/ProgramSeasonsPage';
import DivisionSchedulePage from './pages/programs/DivisionSchedulePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import PublicProgramDirectoryPage from './pages/public/PublicProgramDirectoryPage';
import PublicProgramPage from './pages/public/PublicProgramPage';
import PublicDivisionPage from './pages/public/PublicDivisionPage';
import PublicTeamPage from './pages/public/PublicTeamPage';
import './App.css';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: '#2ea84b', light: '#3ec45e', dark: '#1f7835' },
    secondary:  { main: '#f5a623', light: '#f7bc57', dark: '#c2821b' },
    background: { default: 'transparent', paper: 'rgba(30,41,59,0.85)' },
    text:       { primary: '#f1f5f9', secondary: '#94a3b8' },
    divider:    '#1e3450',
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    body1: { letterSpacing: '-0.005em' },
    body2: { letterSpacing: '-0.005em' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 700,
          letterSpacing: '-0.01em',
          transition: 'all 0.18s cubic-bezier(0.4,0,0.2,1)',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #2ea84b 0%, #24903e 100%)',
          boxShadow: '0 2px 12px rgba(46,168,75,0.30)',
          '&:hover': {
            background: 'linear-gradient(135deg, #35c058 0%, #2ea84b 100%)',
            boxShadow: '0 4px 20px rgba(46,168,75,0.45)',
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #f5a623 0%, #d48c18 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #f7bc57 0%, #f5a623 100%)',
            transform: 'translateY(-1px)',
          },
        },
        outlinedPrimary: {
          borderColor: 'rgba(46,168,75,0.5)',
          '&:hover': {
            borderColor: '#2ea84b',
            backgroundColor: 'rgba(46,168,75,0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundImage: 'none',
          border: '1px solid rgba(26,46,68,0.8)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.2)',
          transition: 'box-shadow 0.2s, border-color 0.2s',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          letterSpacing: '-0.01em',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          letterSpacing: '-0.01em',
          fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(26,46,68,0.6)',
          fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
        },
        head: {
          fontWeight: 700,
          letterSpacing: '0.02em',
          fontSize: '0.72rem',
          color: '#6a8aa8',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(5,12,24,0.97)',
          border: '1px solid #2a4560',
          borderRadius: 8,
          fontSize: '0.78rem',
          fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#1e3450' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
  },
});

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{
        html: { backgroundColor: '#000000', minHeight: '100%' },
        body: { backgroundColor: '#000000', minHeight: '100%' },
        '#root': { backgroundColor: 'transparent', position: 'relative' },
      }} />
      <AuthProvider>
        {/* Subtle grid pattern background */}
        {/* Site-wide matrix rain background */}
        <MatrixRain />
        <FootballPass />

        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(6,182,212,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <BrowserRouter>
            {/* Global modals + admin tools — single instances */}
            <AuthModal />
            <UpgradeModal />
            <AdminViewBar />

            <Routes>
              {/* Main routes */}
              <Route path="/"              element={<FFNLandingPage />} />
              <Route path="/nfl"           element={<NFLPage />} />
              <Route path="/teams"         element={<NFLPage />} />   {/* /teams → Stats Hub, Teams tab */}
              <Route path="/team/:teamId"  element={<TeamPage />} />
              <Route path="/in-the-works"  element={<InTheWorksPage />} />
              <Route path="/pricing"       element={<PricingPage />} />
              <Route path="/upgrade-success" element={<UpgradeSuccessPage />} />
              <Route path="/admin"         element={<AdminPage />} />
              <Route path="/college"       element={<CollegePage />} />
              <Route path="/tools"             element={<ToolsPage />} />
              <Route path="/tools/qb-compare" element={<QBComparePage />} />
              <Route path="/fantasy"       element={<FantasyPage />} />

              {/* Phase C.2 — public Program/Division/Team pages. NONE of
                  these are wrapped in RequireAuth — every one of them
                  must load for a signed-out visitor, per this phase's
                  own "Public Access Rule." Every read on these pages
                  goes through migration 038's public-safe RPCs
                  (publicProgramService.js), never a direct table query.
                  /programs/:programSlug/seasons/:seasonYear reuses the
                  SAME PublicProgramPage component as the bare Program
                  route — seasonYear just selects which Season's data
                  loads (see that component's own header note). Division
                  route uses the Division's own id (UUID), never a slug —
                  a Division always belongs to exactly one Season, so its
                  id alone is already season-safe; Team route likewise
                  uses id rather than the optional/nullable Team slug. */}
              <Route path="/programs" element={<PublicProgramDirectoryPage />} />
              <Route path="/programs/:programSlug" element={<PublicProgramPage />} />
              <Route path="/programs/:programSlug/seasons/:seasonYear" element={<PublicProgramPage />} />
              <Route path="/programs/:programSlug/divisions/:divisionId" element={<PublicDivisionPage />} />
              <Route path="/programs/:programSlug/teams/:teamId" element={<PublicTeamPage />} />

              {/* Account / auth */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Player Performance & Development — V0.1A */}
              {/* Getting Started is a launchpad, not a gate — reached only
                  as the explicit destination of a fresh sign-up (see
                  AuthModal), never enforced/redirected-to on every route.
                  /onboarding is a passive alias for stale bookmarks/links
                  to the old role-questionnaire page it replaced. */}
              <Route path="/getting-started" element={<GettingStartedPage />} />
              <Route path="/onboarding"    element={<Navigate to="/getting-started" replace />} />
              <Route path="/athletes"      element={<MyAthletesPage />} />
              <Route path="/athletes/join-team" element={<JoinTeamPage />} />
              {/* Shareable Team Join Code invite link (017) — same page as
                  the manual-entry route above, pre-filled from the URL.
                  Kept as its own top-level route (not nested under
                  /athletes) to match the short, shareable
                  footballfornerds.com/join/<CODE> form. */}
              <Route path="/join/:code"    element={<JoinTeamPage />} />
              <Route path="/athletes/new"  element={<CreateAthletePage />} />
              <Route path="/athletes/:athleteId" element={<AthleteProfilePage />} />
              <Route path="/athletes/:athleteId/edit" element={<EditAthletePage />} />
              <Route path="/athletes/:athleteId/tests/new" element={<TestingSessionFormPage />} />
              <Route path="/athletes/:athleteId/tests/:sessionId/edit" element={<TestingSessionFormPage />} />
              <Route path="/athletes/:athleteId/test" element={<PersonalTestingPage />} />

              {/* Team + Coaches/Staff — V0.1B.
                  Note: /teams and /team/:teamId are already taken by the
                  existing NFL Stats Hub (see above), so this feature lives
                  under /my-teams to avoid colliding with it.
                  Team Hub (030) — TeamDetailPage is now the Hub's Overview
                  route, unchanged path (/my-teams/:teamId), just its own
                  content wrapped in the shared TeamHubLayout. Roster/Staff
                  keep their existing routes/pages, also now wrapped in
                  TeamHubLayout for a consistent Hub feel — no new roster/
                  staff routes were created. Schedule/Team Board/Playbook
                  are new destinations added under the same
                  /my-teams/:teamId prefix, not a new route family.
                  Schedule (031) is now a real, functional page
                  (TeamSchedulePage.js). Team Board (032) is also a real,
                  functional page (TeamBoardPage.js). Playbook Phase A
                  (034) is also now real — TeamPlaybookPage.js — the
                  reusable Formation/Front/Unit library only; the future
                  Play Designer/PTP/Film Study are later phases.
                  TeamHubPlaceholderPages.js no longer has any tab left
                  to serve and has been removed. */}
              <Route path="/my-teams"      element={<MyTeamsPage />} />
              <Route path="/my-teams/new"  element={<CreateTeamPage />} />
              <Route path="/my-teams/:teamId" element={<TeamDetailPage />} />
              <Route path="/my-teams/:teamId/staff" element={<TeamStaffPage />} />
              <Route path="/my-teams/:teamId/settings" element={<TeamSettingsPage />} />
              <Route path="/my-teams/:teamId/roster" element={<TeamRosterPage />} />
              <Route path="/my-teams/:teamId/roster/:teamAthleteId" element={<RosteredAthletePage />} />
              {/* Phase C.7a — Manual Team Testing Station: an authorized
                  Team Admin/Head Coach records real test_attempts rows
                  for actively-rostered athletes, via the same canonical
                  performance model (test_types/testing_sessions/
                  test_attempts, 005/048) the guardian workflow already
                  uses — see record_team_test_attempt() (049). */}
              <Route path="/my-teams/:teamId/testing" element={<TeamTestingPage />} />
              <Route path="/my-teams/:teamId/schedule" element={<TeamSchedulePage />} />
              <Route path="/my-teams/:teamId/board" element={<TeamBoardPage />} />
              <Route path="/my-teams/:teamId/playbook" element={<TeamPlaybookPage />} />

              {/* Program Management — first user-facing pass (022/023).
                  Nested under /my-programs, deliberately not colliding
                  with /my-teams — a Program-created Season Team is still
                  a plain teams row, so it's opened via the EXISTING
                  /my-teams/:teamId page, not a duplicate one. */}
              <Route path="/my-programs"     element={<MyProgramsPage />} />
              <Route path="/my-programs/new" element={<CreateProgramPage />} />
              <Route path="/my-programs/:programId" element={<ProgramDashboardPage />} />
              <Route path="/my-programs/:programId/new-team" element={<CreateSeasonTeamPage />} />
              <Route path="/my-programs/:programId/manage" element={<ManageProgramPage />} />
              {/* Phase C.1 — Program/Season/Division/Team hierarchy
                  admin view. See ProgramSeasonsPage.js's own header for
                  why this is a separate page from ProgramDashboardPage
                  rather than a rework of its existing Setup/Current/
                  History season_status grouping. */}
              <Route path="/my-programs/:programId/seasons" element={<ProgramSeasonsPage />} />
              {/* Phase C.4a — the private Division Schedule VIEW, an
                  aggregation over existing team_events/team_matchups
                  (042) per Division Team; see DivisionSchedulePage.js's
                  own header. Not a new event-storage model. */}
              <Route path="/my-programs/:programId/divisions/:divisionId/schedule" element={<DivisionSchedulePage />} />

              {/* Phase C.1b — the smallest sensible home for the Career
                  Mode selector (see AccountSettingsPage.js's own header).
                  Not nested under /my-programs — this is account-level,
                  not Program-level. */}
              <Route path="/account" element={<AccountSettingsPage />} />

              {/* Legacy redirects */}
              <Route path="/dashboard"     element={<Navigate to="/nfl" replace />} />
              <Route path="/nfl-draft"     element={<Navigate to="/nfl" replace />} />
              <Route path="*"              element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </Box>
      </AuthProvider>
    </ThemeProvider>
  );
}
